<script>
import { mapGetters } from 'vuex'
import _ from 'underscore'
import Choices from 'choices.js'

import ReviewsListItem from "@components/reviews/reviews-list-item.vue";
import Translatable from '@src/mixins/translatable.js'

export default {
  components: {
    ReviewsListItem
  },
  mixins: [
    Translatable
  ],
  data(){
    return {
      currentPage: 1,
      reviewsPerPage: 10,
      pageRange: 2,
      choice: null,
      reviewsOrdering: 'date'
    }
  },
  computed: {
    ...mapGetters('reviews', [
      'reviewsCards'
    ]),
    ...mapGetters('translations', [
      'isTranslated',
    ]),
    orderedReviews() {
      switch (this.reviewsOrdering) {
        case 'date':
          return _.sortBy(this.reviewsCards, review => Date.parse(review.date)).reverse()
        case 'rating':
          return _.sortBy(( _.sortBy(this.reviewsCards, review =>
            Date.parse(review.date))), review =>
            parseInt(review.rating)).reverse()
        default:
          return this.reviewsCards
      }
    },
    paginatedReviws() {
      const from = (this.currentPage - 1) * this.reviewsPerPage;
      const to = from + this.reviewsPerPage;

      return this.orderedReviews.slice(from, to);
    },
    pages() {
      const pages = []
      for (let i = this.rangeStart; i <= this.rangeEnd; i++) {
        pages.push(i);
      }

      return pages
    },
    hasFirst() {
      return this.rangeStart !== 1
    },
    hasLast() {
      return this.rangeEnd < this.totalPages
    },
    totalPages() {
      return Math.ceil(this.orderedReviews.length / this.reviewsPerPage)
    },
    rangeStart() {
      const start = this.currentPage - this.pageRange

      return (start > 0) ? start : 1
    },
    rangeEnd() {
      const end = this.currentPage + this.pageRange

      return (end < this.totalPages) ? end : this.totalPages
    },
  },
  mounted() {
    this.choice = new Choices('#filter-dropdown', {
      silent: false,
      removeItems: false,
      searchEnabled: false,
      noChoicesText: '',
      choices: [
        {
          label: this.getTranslatedContent('REVIEWS_order_by_rating'),
          value: 'rating',
        },
        {
          label: this.getTranslatedContent('REVIEWS_order_by_date'),
          value: 'date',
          selected: true
        },
      ],
      classNames: {
        containerOuter: 'filter-dropdown',
        containerInner: 'inner-container',
        itemSelectable: 'selected-filter',
        openState: 'select-opened',
        listDropdown: 'filters-container',
        list: 'filters-list'
      }
    })

    this.$refs.orderDropdown.addEventListener('change', data => {
      this.reviewsOrdering = data.detail.value
    })
  },
  updated() {
    if (this.isTranslated) {
      this.setReviewsOrder()

      this.$store.commit('translations/SET_IS_TRANSLATED', false)
    }
  },
  methods: {
    changePage(page) {
      this.scrollToElement({behavior: 'smooth'})
      this.currentPage = page
    },
    scrollToElement(options) {
      const reviewsList = document.querySelector('.reviews')

      if (reviewsList) {
        reviewsList.scrollIntoView(options);
      }
    },
    setReviewsOrder(){
      this.choice.clearChoices()
      this.choice.setChoices(
        [
          {
            label: this.getTranslatedContent('REVIEWS_order_by_rating'),
            value: 'rating',
          },
          {
            label: this.getTranslatedContent('REVIEWS_order_by_date'),
            value: 'date',
            selected: true
          },
        ]
      )
    },
  },
}
</script>

<template>
  <div class="content">
    <select id="filter-dropdown" ref="orderDropdown" class="filter-dropdown"></select>
    <div>{{ getTranslatedContent('') }}</div>
    <div v-for="review in paginatedReviws" :key="review._id" class="card">
      <ReviewsListItem :review="review"/>
    </div>
    <div class="pagination">
      <ul>
        <li><a @click.prevent="changePage(currentPage - 1)" class="previous" :class="currentPage === 1 && 'disabled'"></a></li>
        <li v-if="hasFirst"><a href="#" @click.prevent="changePage(1)">1</a></li>
        <li v-if="hasFirst">...</li>
        <li v-for="page in pages" :key="page">
          <a href="#" @click.prevent="changePage(page)" :class="{ 'active': page === currentPage }">
            {{ page }}
          </a>
        </li>
        <li v-if="hasLast">...</li>
        <li v-if="hasLast"><a href="#" @click.prevent="changePage(totalPages)">{{ totalPages }}</a></li>
        <li><a @click.prevent="changePage(currentPage + 1)" class="next" :class="currentPage === totalPages && 'disabled'"></a></li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.content {
  display: flex;
  flex-direction: column;
}

::v-deep .filter-dropdown {
  align-self: flex-end;
  width: 238px;
  height: 37px;
  background: $white;
  border: 1px solid $dark-gray;
  border-radius: 5px;
  box-sizing: border-box;
  margin-bottom: 30px;
  position: relative;
  cursor: pointer;

  & .inner-container {
    padding: 6px 17px 6px 15px;
    height: 100%;
    box-sizing: border-box;

    & .filters-list {
      & .selected-filter {
        font-family: Open Sans, sans-serif;
        font-weight: normal;
        font-size: 14px;
        line-height: 23px;
        color: $black;
      }
    }
  }

  & .filters-container {
    position: absolute;
    top: 100%;
    width: 100%;
    border-radius: 0 0 5px 5px;
    background: $white;
    visibility: hidden;
    z-index: 1;

    & .filters-list {
      & div {
        font-family: Open Sans, sans-serif;
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 23px;
        color: $black;
        padding-top: 3px;
        padding-bottom: 8px;
        padding-left: 15px;
        position: relative;

        &:before {
          content: '';
          width: 215px;
          height: 1px;
          background-color: $light-gray;
          position: absolute;
          top: 0;
          right: 10px;
        }

        &:hover {
          background-color: $light-gray;
        }

        &:last-child {
          padding-bottom: 10px;

          &:hover {
            border-radius: 0 0 5px 5px;
          }
        }
      }

      & .has-no-choices {
        padding: 0 !important;
      }
    }
  }

  &.select-opened .filters-container {
    border-bottom: 1px solid $yellow;
    border-left: 1px solid $yellow;
    border-right: 1px solid $yellow;
    visibility: visible;
    margin-left: -1px;
  }

  &::after {
    content: '';
    background: url("../../assets/icons/select-arrow-down.svg") center no-repeat;
    width: 8px;
    height: 6px;
    position: absolute;
    top: 15px;
    right: 17px;
    cursor: pointer;
    z-index: 10;
  }

  &.select-opened {
    background: $white;
    box-sizing: border-box;
    border-radius: 5px;

    &:after {
      background: url("../../assets/icons/select-arrow-up.svg") center no-repeat;
    }
  }
}

::v-deep .select-opened {
  border-top: 1px solid $yellow;
  border-left: 1px solid $yellow;
  border-right: 1px solid $yellow;
  border-radius: 5px 5px 0 0 !important;
}

.pagination ul, a {
  text-decoration: none;
  font-family: Open Sans Semibold, sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 21px;
  color: $gray;
  margin-top: 15px;
  user-select: none;

  &.active {
    color: $orange;
  }

  & a:hover {
    color: $orange;
    opacity: 0.8;
  }

  :focus {
    outline: none;
  }
}

.pagination ul li {
  display: inline;
  margin: 0 5px;
}

.arrow-mixin {
  width: 26px;
  height: 26px;
  position: relative;
  top: 7px;
  display: inline-block;
  transform: scale(0.6);
  cursor: pointer;
}

.previous {
  @extend .arrow-mixin;
  background: url("../../assets/icons/left-arrow.svg") no-repeat center;

  &:hover {
    background: url("../../assets/icons/left-arrow-orange.svg") no-repeat center;
    opacity: 0.8;
  }
}

.next {
  @extend .arrow-mixin;
  background: url("../../assets/icons/right-arrow.svg") no-repeat center;

  &:hover {
    background: url("../../assets/icons/right-arrow-orange.svg") no-repeat center;
    opacity: 0.8;
  }
}

.disabled {
  pointer-events: none;
  opacity: 0;
}

.card {
  display: flex;
  min-height: 122px;
  height: auto;
  border-bottom: 1px solid $dark-gray;
  padding: 20px 0 20px 50px;
}

@media only screen and (max-width: $screen-width-xs) {
  .content {
    width:  100%;
    justify-content: center;
  }

  ::v-deep .filter-dropdown {
    width: 100%;
    margin-bottom: 0;
  }

  .pagination ul {
    margin-bottom: 10px;
  }

  .pagination ul li {
    display: inline;
    margin: 0 3px;

    &:last-child, &:first-child {
      margin: 0 6px;
    }
  }

  .next {
    &:hover {
      background: url("../../assets/icons/right-arrow.svg") no-repeat center;
      opacity: 0.8;
    }

    &:active {
      background: url("../../assets/icons/right-arrow-orange.svg") no-repeat center;
    }
  }

  .previous {
    &:hover {
      background: url("../../assets/icons/left-arrow.svg") no-repeat center;
      opacity: 0.8;
    }

    &:active {
      background: url("../../assets/icons/left-arrow-orange.svg") no-repeat center;
    }
  }
  .card {
    flex-direction: row;
    padding: 0;
  }
}
</style>
